import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { AvEmptyStateComponent } from '../../../../Shared/Components/av-empty-state/av-empty-state.component';
import { AvStatusChipComponent } from '../../../../Shared/Components/av-status-chip/av-status-chip.component';
import { PlanDetailFacade } from '../../../../../Facades/Plans/plan-detail.facade';
import { FEATURE_AGENT_KEY } from '../../../../../Consts/plans.const';
import { PlanFeature } from '../../../../../Models/backoffice/plan-feature.model';

@Component({
  selector: 'av-plan-features',
  standalone: true,
  imports: [
    MatIconModule,
    MatTooltipModule,
    TranslateModule,
    AvEmptyStateComponent,
    AvStatusChipComponent,
  ],
  templateUrl: './plan-features.component.html',
  styleUrl: './plan-features.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'd-block' },
})
export class PlanFeaturesComponent {
  readonly facade = inject(PlanDetailFacade);

  readonly agentKey = FEATURE_AGENT_KEY;

  private readonly _selection = signal<readonly string[]>([]);

  readonly isEmpty = computed<boolean>(
    () => this.facade.featuresState() === 'ready' && this.facade.features().length === 0,
  );

  readonly isDirty = computed<boolean>(
    () =>
      [...this._selection()].sort().join('|') !==
      [...this.facade.grantedFeatureIds()].sort().join('|'),
  );

  constructor() {
    this._registerSelectionSync();
  }

  isSelected(feature: PlanFeature): boolean {
    return this._selection().includes(feature.id);
  }

  toggle(feature: PlanFeature): void {
    this._applyToggle(feature.id);
  }

  save(): void {
    this.facade.syncFeatures([...this._selection()]);
  }

  reset(): void {
    this._applySelection(this.facade.grantedFeatureIds());
  }

  private _registerSelectionSync(): void {
    effect(() => {
      const granted = this.facade.grantedFeatureIds();

      this._applySelection(granted);
    });
  }

  private _applySelection(granted: readonly string[]): void {
    this._selection.set([...granted]);
  }

  private _applyToggle(featureId: string): void {
    this._selection.update((current) =>
      current.includes(featureId)
        ? current.filter((id) => id !== featureId)
        : [...current, featureId],
    );
  }
}
